import { SliderInput } from '@/components/SliderInput';
import { PATTERN_NAME, VOLUME_ACCESS_MODES } from '@/utils';
import { Form, Input, Modal, Select } from '@arco-design/web-react';
import { produce } from 'immer';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainerStore } from '../../container';
import { WorkloadComponent } from '../../types';
import {
  isVolumeClaimTemplateItem,
  useStorageClassOptions,
  VolumeClaimTemplateItem,
  VolumeItem,
  VolumeMountItem,
  VolumeMountType,
  VolumeType,
} from '../_hooks';
import { AddedPods } from './added-pods';

const modeOptions = VOLUME_ACCESS_MODES.map((item) => ({
  value: item,
  label: item,
}));

interface AddClaimTemplateModalProps extends WorkloadComponent {
  cluster: string;
  open?: boolean;
  onClose?: () => void;
  onOk?: (values: VolumeClaimTemplateItem) => void;
  data?: VolumeItem;
}

export function AddClaimTemplateModal({
  module,
  cluster,
  open,
  onClose,
  onOk,
  data,
}: AddClaimTemplateModalProps) {
  const { t } = useTranslation();
  const msg = {
    claim_template: t('components:persistent.volume.claim.template'),
    pvc_prefix: t('components:pvc.name.prefix.2'),
    pvc_prefix_desc: t(
      'components:prefix.for.persistent.volume.claim.name.prefix.can.only.contain.lowercase.letters.numbers.and.hyphens.must.start.and.end.with.a.lowercase.letter.or.number.maximum.253.characters',
    ),
    pvc_prefix_error: t(
      'common:invalid.prefix.prefix.can.only.contain.lowercase.letters.numbers.and.hyphens.must.start.and.end.with.a.lowercase.letter.or.number.maximum.253.characters',
    ),
    storage_class: t('common:storage.class'),
    access_modes: t('common:access.mode'),
    volume_capacity: t('common:volume.capacity'),
    no_empty: t('common:validate.not-empty'),
  };

  const { containersArray } = useContainerStore();
  const editData = isVolumeClaimTemplateItem(data) ? data : undefined;
  const { options, loading } = useStorageClassOptions(cluster);
  const initFormValues = useMemo(() => {
    /** 基于添加的容器列表关联名字 */
    const initVolumeMounts: VolumeMountItem[] = containersArray.map((container) => ({
      containerName: container.containerSetting.containerName,
      mountType: VolumeMountType.NotMount,
      mountPath: '',
    }));

    const initialValues: VolumeClaimTemplateItem = {
      type: VolumeType.VOLUME_CLAIM_TEMPLATE,
      volumeName: undefined,
      storageClass: undefined,
      accessModes: ['ReadWriteOnce'],
      volumeCapacity: 1,
      volumeMounts: initVolumeMounts,
    };

    if (editData) {
      const editValues = produce(editData, (draft) => {
        draft.volumeMounts = containersArray.map((container) => {
          const existItem = draft.volumeMounts.find(
            (item) => item.containerName === container.containerSetting.containerName,
          );
          if (existItem) {
            return existItem;
          }
          return {
            containerName: container.containerSetting.containerName,
            mountType: VolumeMountType.NotMount,
            mountPath: '',
          };
        });
      });
      return editValues;
    }
    return initialValues;
  }, [containersArray, editData]);

  const [allValues, setAllValues] = useState<Partial<VolumeClaimTemplateItem>>(initFormValues);
  const [form] = Form.useForm<VolumeClaimTemplateItem>();

  //~ onOk
  const handleOk = async () => {
    try {
      // form 只会收集表单声明的字段, 但是这里面的逻辑有两处并没有参与表单字段的声明, 容器名和type
      await form.validate();
      onOk?.(allValues as VolumeClaimTemplateItem);
    } catch (e) {
      console.error('Error validating form: ', e);
    }
  };

  //~ 关闭 model 清空校验信息
  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
    // 编辑状态下，设置表单值
    if (open && editData) {
      form.setFieldsValue(initFormValues);
    }
  }, [open, form, editData, initFormValues]);

  return (
    <StyledModal visible={open} onCancel={onClose} onOk={handleOk} title={msg.claim_template}>
      <Form
        form={form}
        layout="vertical"
        initialValues={initFormValues}
        onValuesChange={(_, values) => {
          const allValues = {
            ...values,
            type: VolumeType.VOLUME_CLAIM_TEMPLATE,
          } as Partial<VolumeClaimTemplateItem>;
          setAllValues(allValues);
        }}
      >
        <Form.Item
          field="volumeName"
          label={msg.pvc_prefix}
          extra={msg.pvc_prefix_desc}
          rules={[
            { required: true, message: msg.pvc_prefix_error },
            { match: PATTERN_NAME, message: msg.pvc_prefix_error },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          field="storageClass"
          label={msg.storage_class}
          rules={[{ required: true, message: msg.no_empty }]}
        >
          <Select showSearch options={options} loading={loading} />
        </Form.Item>
        <Form.Item
          field="accessModes"
          label={msg.access_modes}
          rules={[{ required: true, message: msg.no_empty }]}
        >
          <Select showSearch mode="multiple" options={modeOptions} />
        </Form.Item>
        <Form.Item
          field="volumeCapacity"
          label={msg.volume_capacity}
          rules={[{ required: true, message: msg.no_empty }]}
        >
          <SliderInput
            min={1}
            max={2048}
            marks={{
              1: '1Gi',
              512: '512Gi',
              1024: '1024Gi',
              1536: '1536Gi',
              2048: '2048Gi',
            }}
            suffix="Gi"
          />
        </Form.Item>
        <AddedPods form={form} />
      </Form>
    </StyledModal>
  );
}

/** ============================ 样式 ==================================== */
const StyledModal = styled(Modal)`
  width: 800px;
  .arco-modal-content {
    min-height: 400px;
    max-height: calc(100vh - 200px);
    overflow-y: auto;
  }
`;
